/**
 * Header Nav Background Scroll Tracker
 * 
 * Calculates overlap between `.header__nav-bg` and `.sectional[data-style-set="white"]`
 * elements, setting the `--nav-scroll-progress` CSS custom property.
 * Compatible with Lenis smooth scroll and Barba.js page transitions.
 */

let currentLenisInstance = null;
let activeScrollHandler = null;
let activeResizeHandler = null;
let activeResizeObserver = null;
let animFrameId = null;

/**
 * Initializes the Menu Background scroll progress calculator.
 * @param {object} lenisInstance - Optional Lenis instance for smooth scroll listening.
 * @param {HTMLElement|Document} container - Optional container context (e.g. data.next.container in Barba).
 */
export function initializeMenuBg(lenisInstance = null, container = document) {
    // Clean up any existing listeners before re-initializing
    destroyMenuBg();

    const navBg = document.querySelector('.header__nav-bg');
    if (!navBg) return;

    const header = document.querySelector('.header') || navBg;
    // Target sections inside the current container/document
    const targetContainer = container || document;

    const lightStyleSets = new Set(['white', 'light-green', 'light-grey', 'lighter-grey', 'cream', 'off-white']);

    const updateNavScroll = () => {
        const allSections = targetContainer.querySelectorAll('.sectional, .hero, .section');
        const rawSections = Array.from(allSections).filter(el => {
            const styleSet = el.getAttribute('data-style-set');
            return styleSet && lightStyleSets.has(styleSet);
        });

        // Merge adjacent or overlapping light sections into contiguous light zones
        const rects = rawSections
            .map(el => el.getBoundingClientRect())
            .filter(r => r.height > 0)
            .sort((a, b) => a.top - b.top);

        const zones = [];
        for (const r of rects) {
            if (!zones.length) {
                zones.push({ top: r.top, bottom: r.bottom });
            } else {
                const last = zones[zones.length - 1];
                if (r.top <= last.bottom + 2) {
                    last.bottom = Math.max(last.bottom, r.bottom);
                } else {
                    zones.push({ top: r.top, bottom: r.bottom });
                }
            }
        }

        const navRect = navBg.getBoundingClientRect();
        const navTop = navRect.top;
        const navHeight = navRect.height;
        
        let progress = 0;

        if (navHeight > 0 && zones.length > 0) {
            for (const zone of zones) {
                // 1. Entering top of light zone
                if (navTop + navHeight > zone.top && navTop < zone.top) {
                    const overlap = (navTop + navHeight) - zone.top;
                    progress = Math.min(1, Math.max(0, overlap / navHeight));
                    break;
                }
                
                // 2. Fully inside light zone
                if (navTop >= zone.top && navTop + navHeight <= zone.bottom) {
                    progress = 1;
                    break;
                }
                
                // 3. Exiting bottom of light zone into dark section below
                if (navTop + navHeight > zone.bottom && navTop < zone.bottom) {
                    const exitOverlap = (navTop + navHeight) - zone.bottom;
                    const exitProgress = Math.min(1, Math.max(0, exitOverlap / navHeight));
                    progress = 1 + exitProgress; // 1.0 -> 2.0 continuous upward motion
                    break;
                }
            }
        }

        const logoProgress = Math.max(0, Math.min(1, 1 - Math.abs(progress - 1)));

        navBg.style.setProperty('--nav-scroll-progress', progress);
        header.style.setProperty('--nav-scroll-progress', progress);
        header.style.setProperty('--logo-scroll-progress', logoProgress);
    };

    activeScrollHandler = updateNavScroll;
    activeResizeHandler = updateNavScroll;
    currentLenisInstance = lenisInstance;

    // Initial calculation
    updateNavScroll();

    // Attach ResizeObserver to recalculate continuously when navBg height changes
    if (typeof ResizeObserver !== 'undefined') {
        activeResizeObserver = new ResizeObserver(() => {
            updateNavScroll();
        });
        activeResizeObserver.observe(navBg);
    }

    // RAF loop during CSS height transitions (sub-menu expand/collapse)
    const stepAnimation = () => {
        updateNavScroll();
        animFrameId = requestAnimationFrame(stepAnimation);
    };

    const handleTransitionStart = (e) => {
        if (e.target === navBg && e.propertyName === 'height') {
            if (animFrameId) cancelAnimationFrame(animFrameId);
            stepAnimation();
        }
    };

    const handleTransitionEnd = (e) => {
        if (e.target === navBg && e.propertyName === 'height') {
            if (animFrameId) cancelAnimationFrame(animFrameId);
            updateNavScroll();
        }
    };

    navBg.addEventListener('transitionstart', handleTransitionStart);
    navBg.addEventListener('transitionend', handleTransitionEnd);
    navBg.addEventListener('transitioncancel', handleTransitionEnd);

    // Attach scroll listener
    if (lenisInstance) {
        lenisInstance.on('scroll', activeScrollHandler);
    } else {
        window.addEventListener('scroll', activeScrollHandler, { passive: true });
    }

    // Attach resize listener
    window.addEventListener('resize', activeResizeHandler, { passive: true });
}

/**
 * Destroys active menu background scroll & resize listeners.
 */
export function destroyMenuBg() {
    if (animFrameId) {
        cancelAnimationFrame(animFrameId);
        animFrameId = null;
    }

    if (activeResizeObserver) {
        activeResizeObserver.disconnect();
        activeResizeObserver = null;
    }

    if (activeScrollHandler) {
        if (currentLenisInstance) {
            currentLenisInstance.off('scroll', activeScrollHandler);
        } else {
            window.removeEventListener('scroll', activeScrollHandler);
        }
        activeScrollHandler = null;
    }

    if (activeResizeHandler) {
        window.removeEventListener('resize', activeResizeHandler);
        activeResizeHandler = null;
    }

    currentLenisInstance = null;
}